import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Checkbox } from '../ui/checkbox'
import { Alert, AlertDescription } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import {
  useAdminRentIndices,
  useCreateRentIndex,
  useRentIndexImportStatus,
  useRunRentIndexImport,
} from '../../api/leaseRent'

const ALL = '__all__'

interface SeriesResult {
  jurisdiction: string
  index_code: string
  inserted?: string[]
  anomalies?: { period: string; kind: string }[]
  error?: string
}

/** Platform admin: the published rent index values, a justified manual entry, the last import. */
export function RentIndicesSection() {
  const { t } = useTranslation('admin')
  const { data } = useAdminRentIndices()
  const status = useRentIndexImportStatus()
  const runImport = useRunRentIndexImport()
  const [jurisdiction, setJurisdiction] = useState(ALL)
  const [indexCode, setIndexCode] = useState(ALL)
  const [adding, setAdding] = useState(false)

  const rows = useMemo(() => data?.data ?? [], [data])
  const jurisdictions = useMemo(() => [...new Set(rows.map((r) => r.jurisdiction))].sort(), [rows])
  const indices = useMemo(() => [...new Set(rows.map((r) => r.index_code))].sort(), [rows])
  const shown = rows.filter((r) => (jurisdiction === ALL || r.jurisdiction === jurisdiction) && (indexCode === ALL || r.index_code === indexCode))

  const last = status.data?.last
  const series = ((last?.output as { series?: SeriesResult[] } | undefined)?.series ?? []) as SeriesResult[]

  return (
    <section className="space-y-6" data-testid="rent-indices-section">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48 space-y-1">
          <Label>{t('rent_indices.filter_jurisdiction')}</Label>
          <Select value={jurisdiction} onValueChange={setJurisdiction}>
            <SelectTrigger data-testid="rent-indices-filter-jurisdiction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('rent_indices.all')}</SelectItem>
              {jurisdictions.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48 space-y-1">
          <Label>{t('rent_indices.filter_index')}</Label>
          <Select value={indexCode} onValueChange={setIndexCode}>
            <SelectTrigger data-testid="rent-indices-filter-index"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('rent_indices.all')}</SelectItem>
              {indices.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => runImport.mutate({})} disabled={runImport.isPending} data-testid="rent-indices-import-button">
            {t('rent_indices.import_now')}
          </Button>
          <Button onClick={() => setAdding(true)} data-testid="rent-index-add-button">{t('rent_indices.add')}</Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <Table data-testid="rent-indices-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('rent_indices.filter_jurisdiction')}</TableHead>
              <TableHead>{t('rent_indices.filter_index')}</TableHead>
              <TableHead>{t('rent_indices.period')}</TableHead>
              <TableHead className="text-right">{t('rent_indices.value')}</TableHead>
              <TableHead>{t('rent_indices.published_at')}</TableHead>
              <TableHead>{t('rent_indices.source')}</TableHead>
              <TableHead>{t('rent_indices.justification')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">{t('rent_indices.empty')}</TableCell></TableRow>
            )}
            {shown.map((r) => (
              <TableRow key={r.uid} data-testid={`rent-index-row-${r.uid}`}>
                <TableCell>{r.jurisdiction}</TableCell>
                <TableCell>{r.index_code}</TableCell>
                <TableCell>{r.period_code}</TableCell>
                <TableCell className="text-right tabular-nums">{r.value}</TableCell>
                <TableCell>{r.published_at ?? ''}</TableCell>
                <TableCell><Badge variant={r.source === 'manual' ? 'warning' : 'outline'} data-testid="rent-index-source">{t(`rent_indices.${r.source}` as any)}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{r.justification ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 rounded-lg border border-border p-4" data-testid="rent-indices-import-status">
        <h2 className="text-sm font-semibold">{t('rent_indices.import_title')}</h2>
        {!last ? (
          <p className="text-sm text-muted-foreground">{t('rent_indices.import_none')}</p>
        ) : (
          <div className="space-y-1 text-sm">
            <div data-testid="rent-indices-import-state">{t('rent_indices.import_status', { status: last.status })} {last.started_at ?? ''}</div>
            {series.map((s) => (
              <div key={`${s.jurisdiction}-${s.index_code}`}>
                <div>{t('rent_indices.import_inserted', { series: `${s.jurisdiction} ${s.index_code}`, count: s.inserted?.length ?? 0 })}</div>
                {s.error && <div className="text-destructive">{t('rent_indices.import_error', { series: s.index_code, error: s.error })}</div>}
                {(s.anomalies ?? []).map((a) => (
                  <div key={a.period} className="text-warning" data-testid="rent-indices-import-anomaly">
                    {t('rent_indices.import_anomaly', { series: s.index_code, period: a.period, kind: a.kind })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && <RentIndexDialog indices={indices} jurisdictions={jurisdictions} onClose={() => setAdding(false)} />}
    </section>
  )
}

function RentIndexDialog({ indices, jurisdictions, onClose }: { indices: string[]; jurisdictions: string[]; onClose: () => void }) {
  const { t } = useTranslation('admin')
  const create = useCreateRentIndex()
  const [jurisdiction, setJurisdiction] = useState(jurisdictions[0] ?? 'FR')
  const [indexCode, setIndexCode] = useState(indices[0] ?? 'irl')
  const [period, setPeriod] = useState('')
  const [value, setValue] = useState('')
  const [publishedAt, setPublishedAt] = useState('')
  const [justification, setJustification] = useState('')
  const [override, setOverride] = useState(false)
  const [error, setError] = useState<string[]>([])

  const save = async () => {
    if (justification.trim().length < 10) {
      setError([t('rent_indices.justification_required')])
      return
    }
    try {
      await create.mutateAsync({
        body: {
          jurisdiction,
          index_code: indexCode,
          period_code: period,
          value: Number(value),
          published_at: publishedAt || null,
          justification: justification.trim(),
          override_checks: override,
        },
      })
      onClose()
    } catch (e) {
      const p = e as { title?: string; fields?: { name: string; message: string }[] }
      const msgs = (p.fields ?? []).map((f) => `${f.name}: ${f.message}`)
      setError(msgs.length ? msgs : [p.title ?? t('rent_indices.save_failed')])
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rent-index-dialog">
        <DialogHeader><DialogTitle>{t('rent_indices.add_title')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="rent-index-jurisdiction">{t('rent_indices.filter_jurisdiction')}</Label>
            <Input id="rent-index-jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value.toUpperCase())} data-testid="rent-index-jurisdiction-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-index-code">{t('rent_indices.index_code')}</Label>
            <Input id="rent-index-code" value={indexCode} onChange={(e) => setIndexCode(e.target.value.toLowerCase())} data-testid="rent-index-code-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-index-period">{t('rent_indices.period_code')}</Label>
            <Input id="rent-index-period" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="rent-index-period-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-index-value">{t('rent_indices.value')}</Label>
            <Input id="rent-index-value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} data-testid="rent-index-value-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-index-published">{t('rent_indices.published_at')}</Label>
            <Input id="rent-index-published" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} data-testid="rent-index-published-input" />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="rent-index-justification">{t('rent_indices.justification')}</Label>
          <Textarea id="rent-index-justification" value={justification} onChange={(e) => setJustification(e.target.value)} data-testid="rent-index-justification-input" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={override} onCheckedChange={(c) => setOverride(c === true)} data-testid="rent-index-override-checkbox" />
          {t('rent_indices.override')}
        </label>
        {error.length > 0 && (
          <Alert variant="destructive" data-testid="rent-index-error"><AlertDescription>{error.map((m) => <div key={m}>{m}</div>)}</AlertDescription></Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rent-index-cancel-button">{t('rent_indices.cancel')}</Button>
          <Button onClick={() => void save()} disabled={create.isPending} data-testid="rent-index-save-button">{t('rent_indices.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
